// ThemeContext — Lets the user pick one of several visual themes that
// re-skin the whole app (background, surface colors, accent colors).
//
// How the override works
// ----------------------
// The app's Tailwind colors (`bg-cream`, `bg-cream-200`, `text-forest`,
// `text-terracotta`, etc.) are compiled to static hex values, so we
// can't just retint via CSS variables alone. Instead, when a theme
// other than the default is active, we set
// `document.documentElement.dataset.theme = "midnight"` and ship a
// stylesheet (`theme.css`) that re-defines those Tailwind classes
// under `html[data-theme="midnight"] .bg-cream { ... }` etc. The
// overrides only fire when a non-default theme is active, so the
// default code path is completely untouched.
//
// Persistence
// -----------
// Choice lives in localStorage under "hw:theme" so it survives page
// reloads and reopens. We hydrate from localStorage on first mount.
//
// Defaults
// --------
// "sanctuary" is the original Holy Words look. All other themes are
// progressive enhancements layered on top via CSS overrides.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const THEMES = [
  {
    // True-black / OLED-style theme. Pure-black background + pure-
    // white text. Default for the app — gives the YouTube/TikTok-style
    // first impression Holy Words is leaning into. Cream "Sanctuary"
    // and other themes remain pickable from Settings.
    id: "oled",
    name: "Pure Black",
    description: "True black background, pure white text. Saves battery on OLED phones.",
    swatch: ["#000000", "#0A0A0A", "#FFFFFF"],
    badge: "Default",
  },
  {
    id: "sanctuary",
    name: "Sanctuary",
    description: "The original Holy Words look — warm cream, forest green, terracotta.",
    swatch: ["#FAF8F5", "#2A4B42", "#C36442"],
  },
  {
    id: "midnight",
    name: "Midnight Worship",
    description: "Deep night-prayer vibe — navy depths, warm cream, golden accents.",
    swatch: ["#0E1A2B", "#1E2D44", "#E5B86E"],
  },
  {
    // Deep amethyst dark theme — royal purple on near-black with a
    // soft lavender accent. Sits between OLED and Midnight; perfect
    // for late-night journaling and Lent / Advent seasonal vibes.
    id: "amethyst",
    name: "Amethyst Night",
    description: "Deep royal purple on velvet black with lavender accents.",
    swatch: ["#0F0617", "#2A1145", "#B98BFF"],
  },
  {
    // Dark crimson theme — wine-red on near-black with warm amber
    // accents. Communion / Sunday-evening service energy without the
    // OLED severity.
    id: "crimson",
    name: "Crimson Sanctuary",
    description: "Deep wine red on near-black, warm amber accents.",
    swatch: ["#170707", "#3F0F14", "#F5A24A"],
  },
  {
    id: "sunrise",
    name: "Sunrise Praise",
    description: "Morning praise palette — peach, coral, and burnt orange.",
    swatch: ["#FCE9D6", "#E48B5F", "#9C3B1B"],
  },
  {
    id: "royal",
    name: "Royal Gospel",
    description: "Cathedral royalty — deep plum, cream, brushed gold.",
    swatch: ["#F5EFE6", "#4A2660", "#C9982E"],
  },
  {
    id: "cathedral",
    name: "Cathedral Plum",
    description: "Wine-and-velvet evening service vibe.",
    swatch: ["#F2E8E1", "#5A1F3A", "#8B5A2B"],
  },
  {
    id: "moss",
    name: "Forest Cathedral",
    description: "Deep evergreen palette — calming, ancient, prayerful.",
    swatch: ["#EFE9DC", "#13332B", "#7BAE7B"],
  },
];

const Ctx = createContext(null);
const STORAGE_KEY = "hw:theme";
const AUTO_KEY = "hw:theme:auto";

// Suggestion (c): auto-pick a theme by time of day. When the user
// enables "Auto" we pick a theme based on the current hour, locally:
//   05:00–07:59 → sunrise (warm light)
//   08:00–18:59 → sanctuary (default cream day)
//   19:00–21:59 → midnight (navy evening)
//   22:00–04:59 → oled (true black for OLED nighttime reading)
// We only auto-update on a fresh page-load + every 30 minutes, so we
// don't yank the theme out from under someone mid-tap. Users can flip
// the auto switch off at any time from Settings.
function pickByTime() {
  const h = new Date().getHours();
  if (h >= 5 && h < 8)   return "sunrise";
  if (h >= 8 && h < 19)  return "sanctuary";
  if (h >= 19 && h < 22) return "midnight";
  return "oled";
}

function readAuto() {
  try { return localStorage.getItem(AUTO_KEY) === "1"; }
  catch (_) { return false; }
}

function readPersisted() {
  try {
    // If auto-by-time is on, recompute on every load. Otherwise honor
    // whatever the user explicitly picked.
    if (readAuto()) return pickByTime();
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && THEMES.some((t) => t.id === stored)) return stored;
  } catch (_) {}
  // First-time visitors land on Pure Black (OLED) — the user
  // explicitly requested it as the new default. Cream "Sanctuary"
  // is still pickable from the theme switcher.
  return "oled";
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeIdState] = useState(readPersisted);
  const [auto, setAutoState] = useState(readAuto);

  // Push the selection to <html data-theme=…> so theme.css can target
  // it. Also stash to localStorage so it survives reloads. AND toggle
  // the Tailwind `dark` class so any component using `dark:` utility
  // variants (JudgmentSection, Comments, MiniPlayer, etc.) flips
  // properly. Without this `dark` class toggle, Tailwind's class-mode
  // dark utilities never fire even when the user picks OLED/Midnight/
  // Amethyst/Crimson — leaving JudgmentSection on the cream styles
  // even on a black background.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = themeId;
    // App-wide dark mode: always keep Tailwind's `dark` class on the
    // root element so every `dark:` utility variant (JudgmentSection,
    // Comments, MiniPlayer, etc.) renders its dark styles regardless
    // of which color theme the user picks.
    document.documentElement.classList.add("dark");
    try { localStorage.setItem(STORAGE_KEY, themeId); } catch (_) {}
  }, [themeId]);

  // When auto is enabled, re-pick every 30 minutes so the theme drifts
  // with the user's local clock without requiring a refresh. We also
  // re-pick when the tab regains focus — covers the "phone in pocket
  // through sunset" case.
  useEffect(() => {
    if (!auto) return;
    setThemeIdState(pickByTime());
    const id = setInterval(() => setThemeIdState(pickByTime()), 30 * 60 * 1000);
    const onFocus = () => setThemeIdState(pickByTime());
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [auto]);

  // Wrap setThemeId so a manual pick automatically disables Auto.
  // (Otherwise the next interval tick would override the user's
  // explicit choice — a confusing UX.)
  const setThemeId = (id) => {
    setAutoState(false);
    try { localStorage.setItem(AUTO_KEY, "0"); } catch (_) {}
    setThemeIdState(id);
  };

  const setAuto = (on) => {
    try { localStorage.setItem(AUTO_KEY, on ? "1" : "0"); } catch (_) {}
    setAutoState(on);
    if (on) setThemeIdState(pickByTime());
  };

  const value = useMemo(() => ({
    themeId,
    setThemeId,
    theme: THEMES.find((t) => t.id === themeId) || THEMES[0],
    themes: THEMES,
    auto,
    setAuto,
  }), [themeId, auto]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const v = useContext(Ctx);
  if (!v) {
    // Fallback for components rendered outside the provider (e.g. tests)
    return { themeId: "oled", setThemeId: () => {}, theme: THEMES[0], themes: THEMES, auto: false, setAuto: () => {} };
  }
  return v;
}
